import React from "react";
import { ArrowLeft, Loader2, Save, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

function IconActionButton({
  label,
  className,
  children,
  disabled,
  ...props
}: {
  label: string;
} & React.ComponentProps<typeof Button>) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">
          <Button
            type="button"
            size="icon"
            disabled={disabled}
            aria-label={label}
            title={label}
            {...props}
            className={cn("h-9 w-9", className)}
          >
            {children}
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

export interface FormFooterProps {
  onBack?: () => void;
  backDisabled?: boolean;
  backTabIndex?: number;
  onDelete?: () => void;
  deleteDisabled?: boolean;
  deleteTabIndex?: number;
  onSave?: () => void;
  saveDisabled?: boolean;
  saveTabIndex?: number;
  saving?: boolean;
  extra?: React.ReactNode;
}

/** Pies de ABM: Guardar/Eliminar filled muted (ícono blanco); Volver outline con hover muted. Solo ícono. */
export function FormFooter({
  onBack,
  backDisabled,
  backTabIndex,
  onDelete,
  deleteDisabled,
  deleteTabIndex,
  onSave,
  saveDisabled,
  saveTabIndex,
  saving,
  extra,
}: FormFooterProps) {
  return (
    <TooltipProvider delayDuration={400}>
      <div className="flex justify-end gap-2">
        {onBack ? (
          <IconActionButton
            label="Volver"
            variant="outline"
            disabled={backDisabled}
            tabIndex={backTabIndex}
            onClick={onBack}
            className="hover:bg-muted hover:text-foreground hover:ring-2 hover:ring-muted-foreground/25 hover:border-muted-foreground/35"
          >
            <ArrowLeft className="h-4 w-4" />
          </IconActionButton>
        ) : null}
        {onDelete ? (
          <IconActionButton
            label="Eliminar"
            variant="dangerMuted"
            disabled={deleteDisabled}
            tabIndex={deleteTabIndex}
            onClick={onDelete}
          >
            <Trash2 className="h-4 w-4" />
          </IconActionButton>
        ) : null}
        {extra}
        {onSave ? (
          <IconActionButton
            label="Guardar"
            variant="saveMuted"
            disabled={saveDisabled}
            tabIndex={saveTabIndex}
            onClick={onSave}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
          </IconActionButton>
        ) : null}
      </div>
    </TooltipProvider>
  );
}
